import React, { useEffect, useRef, useState, useCallback } from 'react';
import './Research.css';
import bobPng from './assets/bob.png';

function Research() {
  const bannerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const edgeRef = useRef(0); // 0..3
  const tRef = useRef(0);
  const [position, setPosition] = useState({ x: 0, y: 0, rot: 6 });
  const [bannerSize, setBannerSize] = useState({ width: 0, height: 0 });
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });

  const SPEED = 2.2;
  const PADDING = 20;
  const ROTATION_AMPLITUDE = 4;
  const ROTATION_SPEED = 400;

  // Measure banner size
  const measureBanner = useCallback(() => {
    if (bannerRef.current) {
      const rect = bannerRef.current.getBoundingClientRect();
      setBannerSize({ width: rect.width, height: rect.height });
    }
  }, []);

  // Measure viewport size
  const measureViewport = useCallback(() => {
    setViewportSize({
      width: window.innerWidth,
      height: window.innerHeight
    });
  }, []);

  // Update position based on edge and t
  const updatePosition = useCallback((edgeIndex, t, bannerW, bannerH, vw, vh) => {
    let x, y;
    const pad = PADDING;

    switch (edgeIndex) {
      case 0: // Top edge: left to right
        x = pad + t;
        y = pad;
        break;
      case 1: // Right edge: top to bottom
        x = vw - bannerW - pad;
        y = pad + t;
        break;
      case 2: // Bottom edge: right to left
        x = (vw - bannerW - pad) - t;
        y = vh - bannerH - pad;
        break;
      case 3: // Left edge: bottom to top
        x = pad;
        y = (vh - bannerH - pad) - t;
        break;
      default:
        x = pad;
        y = pad;
    }

    // Add rotation wobble
    const rot = ROTATION_AMPLITUDE * Math.sin(performance.now() / ROTATION_SPEED);

    return { x, y, rot };
  }, []);

  // Animation loop
  useEffect(() => {
    if (bannerSize.width === 0 || bannerSize.height === 0 || viewportSize.width === 0) {
      return;
    }

    const animate = () => {
      const edgeIndex = edgeRef.current;
      const t = tRef.current;
      const { width: bannerW, height: bannerH } = bannerSize;
      const { width: vw, height: vh } = viewportSize;
      const pad = PADDING;

      // Calculate max t for current edge
      let maxT;
      if (edgeIndex === 0 || edgeIndex === 2) {
        // Top or bottom edge
        maxT = vw - bannerW - 2 * pad;
      } else {
        // Right or left edge
        maxT = vh - bannerH - 2 * pad;
      }

      // Update t
      let newT = t + SPEED;
      let newEdgeIndex = edgeIndex;

      // Check if we've reached the end of the edge
      if (newT >= maxT) {
        newT = 0;
        newEdgeIndex = (edgeIndex + 1) % 4;
      }

      // Update refs
      edgeRef.current = newEdgeIndex;
      tRef.current = newT;

      // Update position
      const newPos = updatePosition(newEdgeIndex, newT, bannerW, bannerH, vw, vh);
      setPosition(newPos);

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [bannerSize, viewportSize, updatePosition]);

  // Measure viewport on mount and resize
  useEffect(() => {
    measureViewport();

    const handleResize = () => {
      measureViewport();
      // Small delay to ensure DOM has updated
      setTimeout(measureBanner, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [measureViewport, measureBanner]);

  // Measure banner when ref is available
  useEffect(() => {
    if (bannerRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      const measureTimeout = requestAnimationFrame(() => {
        measureBanner();
      });
      return () => cancelAnimationFrame(measureTimeout);
    }
  }, [measureBanner]);

  return (
    <div className="research-page">
      <div
        ref={bannerRef}
        className="construction-banner"
        style={{
          transform: `translate3d(${position.x}px, ${position.y}px, 0) rotate(${position.rot}deg)`
        }}
      >
        <img src={bobPng} className="construction-bob" alt="Under construction" />
        <span className="construction-text">UNDER CONSTRUCTION</span>
      </div>
    </div>
  );
}

export default Research;
